import React, { useEffect, useRef, useState } from "react";
import usePipelinesApi, {
  StageItem,
  GetStagesByPipelineResponse,
  CardItem,
  GetCardByIdResponse,
  LabelItem,
  PipelineItem,
  FilterPipelineCardsResponse,
  CardLossReasonItem
} from "./use-pipelines";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useToast } from "../..";
import {
  CardTitle,
  Container,
  CreateCard,
  Title,
  StagesScroll,
  StagesRow,
  StageColumn,
  StageCardsList,
  Adjustments,
  FilterButtons,
  FilterFields,
  FilterSidebarContent
} from "./styles";
import { Button } from "../..";
import { ProgressSpinner } from "primereact/progressspinner";
import { Sidebar } from "primereact/sidebar";
import { InputText } from "primereact/inputtext";
import { AuthContextType, useAuth } from "../../../auth/auth-provider";
import UpdateCard from "../UpdateCard";
import { LabelChip, LabelMultiSelectWrapper } from "../UpdateCard/styles";
import UpdateLabel from "../UpdateLabel";
import UpdateStage from "../UpdateStage";
import Avatar from "../../../omnichannel/avatar";
import {
  whatsapp,
  facebookDataTable,
  InstagramIcon,
  spaik,
  PipelineConfig,
  PipelineRefresh
} from "../../../assets";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { setShowCreateContact } from "../../../redux/slices/omnichannel";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import UpdatePipeline from "../UpdatePipeline";
import LossReason from "../LossReason";
import WorkspaceUserService from "../../../services/workspaceUserService";
import BotsServices from "../../../services/bots";
import { LuSlidersHorizontal } from "react-icons/lu";

const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};

const SORT_OPTIONS = [
  {
    value: "createdAt-1",
    label: "Criação: mais antigos",
    sortField: "createdAt",
    sortOrder: 1
  },
  {
    value: "createdAt--1",
    label: "Criação: mais recentes",
    sortField: "createdAt",
    sortOrder: -1
  },
  {
    value: "updatedAt-1",
    label: "Atualização: mais antigos",
    sortField: "updatedAt",
    sortOrder: 1
  },
  {
    value: "updatedAt--1",
    label: "Atualização: mais recentes",
    sortField: "updatedAt",
    sortOrder: -1
  },
  {
    value: "name-1",
    label: (
      <>
        Nome: A <i className="pi pi-arrow-right" style={{ fontSize: "10px" }} />{" "}
        Z
      </>
    ),
    sortField: "name",
    sortOrder: 1
  },
  {
    value: "name--1",
    label: (
      <>
        Nome: Z <i className="pi pi-arrow-right" style={{ fontSize: "10px" }} />{" "}
        A
      </>
    ),
    sortField: "name",
    sortOrder: -1
  }
];

const Pipeline = () => {
  const toast = useToast();
  const dispatch = useAppDispatch();
  const [stages, setStages] = useState<StageItem[]>([]);
  const [currentPipelineId, setCurrentPipelineId] = useState<number | null>(
    null
  );
  const [pipelineName, setPipelineName] = useState("");
  const [showConfigMenu, setShowConfigMenu] = useState(false);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [hasMainPipelineLoaded, setHasMainPipelineLoaded] = useState(false);
  const configMenuRef = useRef<HTMLDivElement | null>(null);
  const sortMenuRef = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [showEditStage, setShowEditStage] = useState(false);
  const [showCardSidebar, setShowCardSidebar] = useState(false);
  const [cardMode, setCardMode] = useState<"create" | "edit">("edit");
  const [cardSelected, setCardSelected] = useState<GetCardByIdResponse | null>(
    null
  );
  const workspacesSelected = useAppSelector(
    (state) => state.workspaces.selectedWorkspaces
  );
  const showCreateContact = useAppSelector(
    (state) => state.omnichannel.showCreateContact
  );
  const { getUsersByWorkspace } = WorkspaceUserService();
  const hasSelectedWorkspaces = !!workspacesSelected?.length;
  const [showEditLabel, setShowEditLabel] = useState(false);
  const [labels, setLabels] = useState<LabelItem[]>([]);
  const [attendants, setAttendants] = useState<{ id: number; name: string }[]>(
    []
  );
  const [bots, setBots] = useState<any[]>([]);
  const [usersByWorkspace, setUsersByWorkspace] = useState<
    { id: number; name: string }[]
  >([]);
  const [pipelines, setPipelines] = useState<PipelineItem[]>([]);
  const { user, company, hasAnyPermission } = useAuth() as AuthContextType;
  const { GetBotsWhatsAppQrCode } = BotsServices();
  const [createStageId, setCreateStageId] = useState<number | null>(null);
  const [stagePages, setStagePages] = useState<Record<number, number>>({});
  const [loadingMoreStages, setLoadingMoreStages] = useState<
    Record<number, boolean>
  >({});
  const [finishedStages, setFinishedStages] = useState<Record<number, boolean>>(
    {}
  );
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearchValue = useDebounce(searchValue, 600);
  const companyId = user.admin ? company?.id : user.companyId;
  const [showPipelineSidebar, setShowPipelineSidebar] = useState(false);
  const [showFiltersSidebar, setShowFiltersSidebar] = useState(false);
  const [showLossReasonSidebar, setShowLossReasonSidebar] = useState(false);
  const [lossReasons, setLossReasons] = useState<CardLossReasonItem[]>([]);
  const [pipelineSelected, setPipelineSelected] = useState<PipelineItem | null>(
    null
  );
  const [selectedResponsibleUserIds, setSelectedResponsibleUserIds] = useState<
    number[]
  >([]);
  const [draftResponsibleUserIds, setDraftResponsibleUserIds] = useState<
    number[]
  >([]);
  const [selectedLabelIds, setSelectedLabelIds] = useState<number[]>([]);
  const [draftLabelIds, setDraftLabelIds] = useState<number[]>([]);
  const [selectedSource, setSelectedSource] = useState("");
  const [draftSource, setDraftSource] = useState("");
  const [expandedCardLabels, setExpandedCardLabels] = useState<
    Record<number, boolean>
  >({});
  const [selectedSortOption, setSelectedSortOption] = useState<
    (typeof SORT_OPTIONS)[number] | null
  >(null);
  const hasInitializedWorkspaceSelection = useRef(false);
  const selectedPipeline =
    pipelines.find((pipeline) => pipeline.id === currentPipelineId) ?? null;
  const canCreatePipeline = user.admin || hasAnyPermission("pipeline_create");
  const canEditPipeline = user.admin || hasAnyPermission("pipeline_edit");
  const canManageLabels = hasMainPipelineLoaded && canEditPipeline;
  const canManageStages = hasMainPipelineLoaded && canEditPipeline;
  const canUpdatePipeline = hasMainPipelineLoaded && canEditPipeline;
  const canAddPipeline = canCreatePipeline;
  const canShowConfigMenu =
    canManageLabels || canManageStages || canUpdatePipeline || canAddPipeline;
  const {
    getPipelineByWorkspacesIds,
    getPipelinesByCompanyId,
    getStagesByPipeline,
    listCardsByStage,
    filterCardsByPipeline,
    getCardsById,
    getAllLabels,
    getCardLossReasons,
    changeCardStage
  } = usePipelinesApi();

  const platformOptions: { [key: string]: string } = {
    LIVECHAT: spaik,
    WHATSAPP: whatsapp,
    FACEBOOK: facebookDataTable,
    INSTAGRAM: InstagramIcon
  };

  const sourceOptions = [
    { label: "Instagram", value: "INSTAGRAM" },
    { label: "Facebook", value: "FACEBOOK" },
    { label: "WhatsApp", value: "WHATSAPP" },
    { label: "CRM", value: "CRM" },
    { label: "LiveChat", value: "LIVECHAT" }
  ];

  const labelOptions = labels.map((label) => ({
    label: label.name,
    value: label.id,
    color: label.color
  }));

  const formatSourceLabel = (source?: string) => {
    const sourceLabels: { [key: string]: string } = {
      FACEBOOK: "Facebook",
      CRM: "CRM",
      LIVECHAT: "LiveChat",
      INSTAGRAM: "Instagram",
      WHATSAPP: "WhatsApp"
    };

    return sourceLabels[source ?? ""] || source || "-";
  };

  const formatDate = (value?: string | null) => {
    if (!value) return "-";

    return new Date(value)
      .toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
      })
      .replace(",", " -");
  };

  const resolveSortParams = (
    sortOption: (typeof SORT_OPTIONS)[number] | null = selectedSortOption
  ) => ({
    sortField: sortOption?.sortField ?? "updatedAt",
    sortOrder: sortOption?.sortOrder ?? -1
  });

  const getMainPipeline = async () => {
    const canViewPipeline = user.admin || hasAnyPermission("pipeline_view");

    if (!canViewPipeline) {
      setCurrentPipelineId(null);
      setStages([]);
      setPipelineName("");
      setHasMainPipelineLoaded(false);
      return;
    }

    try {
      setLoading(true);

      const response = (await getPipelineByWorkspacesIds(
        companyId,
        workspacesSelected
      )) as PipelineItem;

      setCurrentPipelineId(response.id);
      setPipelineName(response.name);
      setHasMainPipelineLoaded(true);
    } catch (error: any) {
      setCurrentPipelineId(null);
      setStages([]);
      setPipelineName("");
      setHasMainPipelineLoaded(false);

      const backendMessage =
        error?.message ||
        error?.response?.data?.message ||
        "Erro ao buscar pipeline principal.";

      toast?.current?.show({
        life: 5000,
        severity: "error",
        summary: "Erro",
        detail: backendMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const getFilterUserIds = (userIds = selectedResponsibleUserIds) =>
    userIds.length ? userIds : undefined;

  const getFilterLabelIds = (labelIds = selectedLabelIds) =>
    labelIds.length ? labelIds : undefined;

  const getFilterSource = (source = selectedSource) =>
    source.trim() ? source.trim() : undefined;

  const getAllStages = async (
    sortOption: (typeof SORT_OPTIONS)[number] | null = selectedSortOption,
    userIds = selectedResponsibleUserIds,
    labelIds = selectedLabelIds,
    source = selectedSource
  ) => {
    if (!currentPipelineId) return;

    try {
      setLoading(true);
      const { sortField, sortOrder } = resolveSortParams(sortOption);

      const response = (await getStagesByPipeline(
        currentPipelineId,
        sortField,
        sortOrder,
        getFilterUserIds(userIds),
        getFilterLabelIds(labelIds),
        getFilterSource(source)
      )) as GetStagesByPipelineResponse;

      setPipelineName(response.pipelineName);
      setStages(
        response.stages.slice().sort((a, b) => a.position - b.position)
      );
      setStagePages(
        Object.fromEntries(response.stages.map((stage) => [stage.id, 1]))
      );
      setLoadingMoreStages(
        Object.fromEntries(response.stages.map((stage) => [stage.id, false]))
      );
      setFinishedStages(
        Object.fromEntries(response.stages.map((stage) => [stage.id, false]))
      );
    } catch (error: any) {
      setStages([]);

      const backendMessage =
        error?.message ||
        error?.response?.data?.message ||
        "Erro ao listar stages.";

      toast?.current?.show({
        life: 5000,
        severity: "error",
        summary: "Erro",
        detail: backendMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const normalizedSearchValue = debouncedSearchValue.trim();
  const handleLoadMoreCards = async (stageId: number) => {
    if (loadingMoreStages[stageId]) return;
    if (finishedStages[stageId]) return;

    const nextPage = (stagePages[stageId] ?? 1) + 1;

    try {
      setLoadingMoreStages((prev) => ({
        ...prev,
        [stageId]: true
      }));
      const { sortField, sortOrder } = resolveSortParams();

      const response = (await listCardsByStage(
        stageId,
        nextPage,
        normalizedSearchValue,
        sortField,
        sortOrder,
        getFilterUserIds(),
        getFilterLabelIds(),
        getFilterSource()
      )) as CardItem[] | null;

      if (!response || !response.length) {
        setFinishedStages((prev) => ({
          ...prev,
          [stageId]: true
        }));
        return;
      }

      setStages((prev) =>
        prev.map((stage) =>
          stage.id === stageId
            ? { ...stage, cards: [...(stage.cards ?? []), ...response] }
            : stage
        )
      );

      setStagePages((prev) => ({
        ...prev,
        [stageId]: nextPage
      }));
    } catch (error: any) {
      const backendMessage =
        error?.message ||
        error?.response?.data?.message ||
        "Erro ao listar mais cards da etapa.";

      toast?.current?.show({
        life: 5000,
        severity: "error",
        summary: "Erro",
        detail: backendMessage
      });
    } finally {
      setLoadingMoreStages((prev) => ({
        ...prev,
        [stageId]: false
      }));
    }
  };

  const handleFilterSearch = async (
    value: string,
    sortOption: (typeof SORT_OPTIONS)[number] | null = selectedSortOption,
    userIds = selectedResponsibleUserIds,
    labelIds = selectedLabelIds,
    source = selectedSource
  ) => {
    if (!currentPipelineId) return;

    try {
      const { sortField, sortOrder } = resolveSortParams(sortOption);
      const response = (await filterCardsByPipeline(
        currentPipelineId,
        value,
        sortField,
        sortOrder,
        getFilterUserIds(userIds),
        getFilterLabelIds(labelIds),
        getFilterSource(source)
      )) as FilterPipelineCardsResponse;

      const cardsByStage = new Map<number, CardItem[]>(
        response.stages.map((stage) => [stage.stageId, stage.cards])
      );

      setStages((prev) =>
        prev.map((stage) => ({
          ...stage,
          cards: cardsByStage.get(stage.id) ?? []
        }))
      );

      setStagePages(
        Object.fromEntries(response.stages.map((stage) => [stage.stageId, 1]))
      );

      setFinishedStages(
        Object.fromEntries(
          response.stages.map((stage) => [
            stage.stageId,
            stage.cards.length < 10
          ])
        )
      );
    } catch (error: any) {
      const backendMessage =
        error?.message ||
        error?.response?.data?.message ||
        "Erro ao filtrar cards.";

      toast?.current?.show({
        life: 5000,
        severity: "error",
        summary: "Erro",
        detail: backendMessage
      });
    }
  };

  const hasTypedSearchRef = useRef(false);
  useEffect(() => {
    if (!currentPipelineId) return;

    if (normalizedSearchValue !== "") {
      hasTypedSearchRef.current = true;
    }

    if (!hasTypedSearchRef.current) return;

    handleFilterSearch(normalizedSearchValue);
  }, [normalizedSearchValue, currentPipelineId]);

  const getPipelines = async () => {
    if (!hasSelectedWorkspaces) return;

    const canViewPipeline = user.admin || hasAnyPermission("pipeline_view");

    if (!canViewPipeline) {
      setPipelines([]);
      return;
    }

    try {
      const response = (await getPipelinesByCompanyId(
        companyId,
        workspacesSelected
      )) as PipelineItem[];

      setPipelines(response);
    } catch (error: any) {
      setPipelines([]);

      const backendMessage =
        error?.message ||
        error?.response?.data?.message ||
        "Erro ao listar pipelines.";

      toast?.current?.show({
        life: 5000,
        severity: "error",
        summary: "Erro",
        detail: backendMessage
      });
    }
  };

  const handleGetCardById = async (card: CardItem) => {
    const canViewAll = user.admin || hasAnyPermission("contactPipeline_view");

    const isOwnCard = card.userId != null && card.userId === user.id;

    if (!canViewAll && !isOwnCard) {
      toast?.current?.show({
        life: 5000,
        severity: "warn",
        summary: "Aviso",
        detail: "Você não tem permissão para visualizar este card."
      });
      return;
    }

    try {
      const response = (await getCardsById(card.id)) as GetCardByIdResponse;

      handleGetCardLossReasons();
      setCardSelected(response);
      setCardMode("edit");
      setShowCardSidebar(true);
      handleGetWhatsappQrCodeBots();
    } catch (error: any) {
      const backendMessage =
        error?.message ||
        error?.response?.data?.message ||
        "Erro ao buscar card.";

      toast?.current?.show({
        life: 5000,
        severity: "error",
        summary: "Erro",
        detail: backendMessage
      });
    }
  };

  const handleGetWhatsappQrCodeBots = async () => {
    try {
      if (!companyId || !workspacesSelected?.length) {
        setBots([]);
        return;
      }

      const response = await GetBotsWhatsAppQrCode({
        companyId,
        workspaces: workspacesSelected
      });
      setBots(response ?? []);
    } catch (error: any) {
      setBots([]);

      const backendMessage =
        error?.message ||
        error?.response?.data?.message ||
        "Erro ao listar bots.";

      toast?.current?.show({
        life: 5000,
        severity: "error",
        summary: "Erro",
        detail: backendMessage
      });
    }
  };

  const handleGetCardLossReasons = async () => {
    try {
      if (!companyId) {
        setLossReasons([]);
        return;
      }

      const response = (await getCardLossReasons(
        companyId
      )) as CardLossReasonItem[];

      setLossReasons(response ?? []);
    } catch (error: any) {
      setLossReasons([]);

      const backendMessage =
        error?.message ||
        error?.response?.data?.message ||
        "Erro ao listar motivos de não conversão.";

      toast?.current?.show({
        life: 5000,
        severity: "error",
        summary: "Erro",
        detail: backendMessage
      });
    }
  };

  const handleGetUsersByWorkspace = async () => {
    try {
      const workspaceId = selectedPipeline?.workspaceId;

      if (!companyId || !workspaceId) {
        setUsersByWorkspace([]);
        return;
      }

      const response = await getUsersByWorkspace(companyId, workspaceId);

      setUsersByWorkspace(response as { id: number; name: string }[]);
    } catch (error: any) {
      setUsersByWorkspace([]);

      const backendMessage =
        error?.message ||
        error?.response?.data?.message ||
        "Erro ao listar usuários do workspace.";

      toast?.current?.show({
        life: 5000,
        severity: "error",
        summary: "Erro",
        detail: backendMessage
      });
    }
  };

  const handleChangeCardPosition = async (
    card: CardItem,
    sourceStageId: number,
    destinationStageId: number
  ) => {
    const canEditAll = user.admin || hasAnyPermission("contactPipeline_edit");
    const isOwnCard = card.userId != null && card.userId === user.id;

    if (!canEditAll && !isOwnCard) {
      toast?.current?.show({
        life: 5000,
        severity: "warn",
        summary: "Aviso",
        detail: "Você não tem permissão para mover este card."
      });
      return;
    }

    const previousStages = stages;

    setStages((prev) => {
      let movedCard: CardItem | null = null;

      const stagesWithoutCard = prev.map((stage) => {
        if (stage.id !== sourceStageId) return stage;

        const updatedCards = (stage.cards ?? []).filter((currentCard) => {
          if (currentCard.id === card.id) {
            movedCard = currentCard;
            return false;
          }
          return true;
        });

        return {
          ...stage,
          cards: updatedCards
        };
      });

      if (!movedCard) return prev;

      return stagesWithoutCard.map((stage) => {
        if (stage.id !== destinationStageId) return stage;

        return {
          ...stage,
          cards: [movedCard!, ...(stage.cards ?? [])]
        };
      });
    });

    try {
      await changeCardStage(card.id, destinationStageId);
    } catch (error: any) {
      setStages(previousStages);

      const backendMessage =
        error?.message ||
        error?.response?.data?.message ||
        "Erro ao mover card de etapa.";

      toast?.current?.show({
        life: 5000,
        severity: "error",
        summary: "Erro",
        detail: backendMessage
      });
    }
  };

  const handleDragEnd = (result: any) => {
    const { source, destination, draggableId } = result;

    if (!destination) return;

    const sourceStageId = Number(source.droppableId);
    const destinationStageId = Number(destination.droppableId);
    const cardId = Number(draggableId);

    if (sourceStageId === destinationStageId) return;

    const sourceStage = stages.find((stage) => stage.id === sourceStageId);
    const card = sourceStage?.cards?.find((item) => item.id === cardId);

    if (!card) return;

    handleChangeCardPosition(card, sourceStageId, destinationStageId);
  };

  useEffect(() => {
    const originalError = console.error;
    const originalWarn = console.warn;

    const shouldIgnoreDnDMessage = (args: any[]) => {
      const firstArg = args[0];

      return (
        typeof firstArg === "string" &&
        firstArg.includes(
          "Droppable: unsupported nested scroll container detected"
        )
      );
    };

    console.error = (...args) => {
      if (shouldIgnoreDnDMessage(args)) return;
      originalError(...args);
    };

    console.warn = (...args) => {
      if (shouldIgnoreDnDMessage(args)) return;
      originalWarn(...args);
    };

    return () => {
      console.error = originalError;
      console.warn = originalWarn;
    };
  }, []);

  useEffect(() => {
    getAllStages();
  }, [currentPipelineId]);

  const createCard = async (stageId: number) => {
    setCreateStageId(stageId);
    setCardSelected(null);
    setCardMode("create");
    dispatch(setShowCreateContact(true));
    handleGetCardLossReasons();
    handleGetWhatsappQrCodeBots();
  };

  const getAllPipelineLabels = async () => {
    try {
      const response = (await getAllLabels(companyId)) as LabelItem[];
      setLabels(response);
    } catch (error: any) {
      setLabels([]);

      const backendMessage =
        error?.message ||
        error?.response?.data?.message ||
        "Erro ao listar labels.";

      toast?.current?.show({
        life: 5000,
        severity: "error",
        summary: "Erro",
        detail: backendMessage
      });
    }
  };

  useEffect(() => {
    setPipelines([]);

    if (!hasSelectedWorkspaces) {
      setCurrentPipelineId(null);
      setPipelineName("");
      setStages([]);
      setHasMainPipelineLoaded(false);
      setShowEditStage(false);
      setShowEditLabel(false);

      if (hasInitializedWorkspaceSelection.current) {
        toast?.current?.show({
          life: 5000,
          severity: "warn",
          summary: "Aviso",
          detail: "Não é possível buscar pipelines sem um workspace selecionado"
        });
      } else {
        hasInitializedWorkspaceSelection.current = true;
      }

      return;
    }

    hasInitializedWorkspaceSelection.current = true;
    getMainPipeline();
    getPipelines();
  }, [companyId, workspacesSelected]);

  useEffect(() => {
    if (!showCreateContact) return;

    setCardSelected(null);
    setCardMode("create");
    setShowCardSidebar(true);
    handleGetCardLossReasons();
    handleGetWhatsappQrCodeBots();
  }, [showCreateContact]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        configMenuRef.current &&
        !configMenuRef.current.contains(event.target as Node)
      ) {
        setShowConfigMenu(false);
      }

      if (
        sortMenuRef.current &&
        !sortMenuRef.current.contains(event.target as Node)
      ) {
        setShowSortMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleOpenLabels = async () => {
    setShowConfigMenu(false);
    setShowEditLabel(true);
  };

  const handleOpenStages = () => {
    setShowConfigMenu(false);
    setShowEditStage(true);
  };

  const handleEditPipeline = () => {
    setShowConfigMenu(false);

    const selectedPipeline =
      pipelines.find((pipeline) => pipeline.id === currentPipelineId) ?? null;

    setPipelineSelected(selectedPipeline);
    setShowPipelineSidebar(true);
  };

  const handleAddPipeline = () => {
    setShowConfigMenu(false);
    setPipelineSelected(null);
    setShowPipelineSidebar(true);
  };

  const handleOpenLossReason = () => {
    setShowConfigMenu(false);
    handleGetCardLossReasons();
    setShowLossReasonSidebar(true);
  };

  const handleOpenFilters = () => {
    setDraftResponsibleUserIds(selectedResponsibleUserIds);
    setDraftLabelIds(selectedLabelIds);
    setDraftSource(selectedSource);
    setShowFiltersSidebar(true);
    handleGetUsersByWorkspace();
  };

  const handleApplyFilters = () => {
    const userIds = draftResponsibleUserIds;
    const labelIds = draftLabelIds;
    const source = draftSource;

    setSelectedResponsibleUserIds(userIds);
    setSelectedLabelIds(labelIds);
    setSelectedSource(source);
    setShowFiltersSidebar(false);

    if (normalizedSearchValue !== "" || hasTypedSearchRef.current) {
      handleFilterSearch(
        normalizedSearchValue,
        selectedSortOption,
        userIds,
        labelIds,
        source
      );
      return;
    }

    getAllStages(selectedSortOption, userIds, labelIds, source);
  };

  const handleClearFilters = () => {
    const hasAppliedFilters =
      selectedResponsibleUserIds.length > 0 ||
      selectedLabelIds.length > 0 ||
      selectedSource.trim() !== "";

    setDraftResponsibleUserIds([]);
    setDraftLabelIds([]);
    setDraftSource("");
    setSelectedResponsibleUserIds([]);
    setSelectedLabelIds([]);
    setSelectedSource("");
    setShowFiltersSidebar(false);

    if (!hasAppliedFilters) return;

    if (normalizedSearchValue !== "" || hasTypedSearchRef.current) {
      handleFilterSearch(normalizedSearchValue, selectedSortOption, [], [], "");
      return;
    }

    getAllStages(selectedSortOption, [], [], "");
  };

  const handleSelectSortOption = (
    sortOption: (typeof SORT_OPTIONS)[number]
  ) => {
    setSelectedSortOption(sortOption);
    setShowSortMenu(false);

    if (normalizedSearchValue !== "" || hasTypedSearchRef.current) {
      handleFilterSearch(normalizedSearchValue, sortOption);
      return;
    }

    getAllStages(sortOption);
  };

  const handleRefreshStages = () => {
    if (normalizedSearchValue !== "") {
      setLoading(true);
      handleFilterSearch(normalizedSearchValue).finally(() => {
        setLoading(false);
      });
      return;
    }

    getAllStages();
  };

  useEffect(() => {
    if (!hasSelectedWorkspaces) return;

    getAllPipelineLabels();
  }, [companyId, hasSelectedWorkspaces]);

  const toggleCardLabels = (cardId: number) => {
    setExpandedCardLabels((prev) => ({
      ...prev,
      [cardId]: !prev[cardId]
    }));
  };

  const isLightColor = (color?: string) => {
    if (!color) return false;

    const hex = color.replace("#", "");

    const normalized =
      hex.length === 3
        ? hex
            .split("")
            .map((char) => char + char)
            .join("")
        : hex;

    if (normalized.length !== 6) return false;

    const r = parseInt(normalized.slice(0, 2), 16);
    const g = parseInt(normalized.slice(2, 4), 16);
    const b = parseInt(normalized.slice(4, 6), 16);

    const brightness = (r * 299 + g * 587 + b * 114) / 1000;

    return brightness > 220;
  };

  const renderCard = (card: CardItem) => (
    <div onClick={() => handleGetCardById(card)} className="contactCard">
      {!!card.labelIds?.length && (
        <div
          className={`tags ${expandedCardLabels[card.id] ? "expanded" : ""}`}
          onClick={(event) => {
            event.stopPropagation();
            toggleCardLabels(card.id);
          }}
        >
          {labels
            .filter((label) => card.labelIds?.includes(label.id))
            .map((label) => (
              <div
                key={label.id}
                className="tag"
                style={{
                  background: label.color,
                  color:
                    expandedCardLabels[card.id] && isLightColor(label.color)
                      ? "#000"
                      : undefined
                }}
              >
                {expandedCardLabels[card.id] ? label.name : null}
              </div>
            ))}
        </div>
      )}
      <div className="name">
        <div style={{ position: "relative" }}>
          <Avatar name={card.contactName} />

          <div
            data-pr-position="bottom"
            style={{
              display: "flex",
              alignItems: "center",
              position: "absolute",
              bottom: 0,
              right: 5
            }}
          >
            {platformOptions[card.source] ? (
              <img
                src={platformOptions[card.source]}
                height={15}
                width={platformOptions[card.source] === spaik ? 20 : 15}
              />
            ) : null}
          </div>
        </div>

        <div>{card.contactName}</div>
      </div>
      <div className="divisor">
        <div className="infos">
          <div className="info">
            <div className="title">Origem:</div>
            <div className="textInfo">{formatSourceLabel(card.source)}</div>
          </div>

          <div className="info">
            <div className="title">Responsável:</div>
            <div className="textInfo">
              {card.userName || card.botName || "Sem responsável"}
            </div>
          </div>
        </div>

        <div className="data">
          <div className="title">Criado em:</div>
          <div>{formatDate(card.createdAt)}</div>
        </div>
      </div>
    </div>
  );

  return (
    <Container>
      <style>
        {labelOptions
          .map(
            (label, index) => `
              .label-multiselect-panel .p-multiselect-items .p-multiselect-item:nth-child(${
                index + 1
              }) {
                background: ${label.color} !important;
                color: ${
                  isLightColor(label.color) ? "#000" : "#fff"
                } !important;
              }

              .label-multiselect-panel .p-multiselect-items .p-multiselect-item:nth-child(${
                index + 1
              }).p-highlight {
                background: ${label.color} !important;
                color: ${
                  isLightColor(label.color) ? "#000" : "#fff"
                } !important;
              }

              .label-multiselect-panel .p-multiselect-items .p-multiselect-item:nth-child(${
                index + 1
              }):not(.p-highlight):not(.p-disabled):hover {
                background: ${label.color} !important;
                color: ${
                  isLightColor(label.color) ? "#000" : "#fff"
                } !important;
              }
            `
          )
          .join("\n")}
      </style>
      {loading ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <ProgressSpinner
            style={{ width: "100px", height: "100px" }}
            strokeWidth="4"
            fill="var(--surface-ground)"
            animationDuration=".6s"
          />
        </div>
      ) : (
        <>
          <Title>
            <Dropdown
              value={currentPipelineId}
              options={pipelines}
              optionLabel="name"
              optionValue="id"
              placeholder="Selecionar pipeline"
              onShow={() => {
                getPipelines();
              }}
              emptyMessage="Nenhum pipeline encontrado"
              onChange={(e) => {
                const selectedPipeline = pipelines.find(
                  (pipeline) => pipeline.id === e.value
                );

                setCurrentPipelineId(e.value);
                setPipelineName(selectedPipeline?.name ?? "");
              }}
              style={{
                display: "flex",
                alignItems: "center",
                width: "275px",
                height: "50px",
                borderRadius: "8px"
              }}
            />

            <div className="filter">
              <i className="pi pi-search" />
              <InputText
                value={searchValue}
                placeholder="Buscar"
                style={{ border: "none", width: "100%" }}
                onChange={(e) => setSearchValue(e.target.value)}
              />
            </div>

            <Adjustments ref={sortMenuRef}>
              <div
                className="imageDiv sortButton"
                onClick={() => setShowSortMenu((prev) => !prev)}
              >
                <i className="pi pi-sort-alt" style={{ fontSize: "14px" }} />
                <span>{selectedSortOption?.label ?? "Ordenar por"}</span>
              </div>

              {showSortMenu ? (
                <div className="buttons sortButtons">
                  {SORT_OPTIONS.map((sortOption) => (
                    <div
                      key={sortOption.value}
                      onClick={() => handleSelectSortOption(sortOption)}
                      className={`option sortOption${
                        selectedSortOption?.value === sortOption.value
                          ? " selected"
                          : ""
                      }`}
                    >
                      <span>{sortOption.label}</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </Adjustments>

            {hasMainPipelineLoaded ? (
              <div className="filters">
                <div className="filterButton" onClick={handleOpenFilters}>
                  <LuSlidersHorizontal size={18} color="#6b7280" />
                  <div>Filtrar</div>
                </div>

                <Button
                  className="room-managment-button"
                  onClick={handleRefreshStages}
                  style={{ border: "1px solid #ced4da", borderRadius: "8px" }}
                >
                  <img src={PipelineRefresh} style={{ width: "18px" }} />
                </Button>
              </div>
            ) : null}

            {canShowConfigMenu ? (
              <Adjustments ref={configMenuRef}>
                <div
                  className="imageDiv "
                  onClick={() => setShowConfigMenu((prev) => !prev)}
                >
                  <img src={PipelineConfig} />
                </div>

                {showConfigMenu ? (
                  <div className="buttons">
                    {canManageLabels ? (
                      <div onClick={handleOpenLabels} className="option">
                        <i className="pi pi-tag" />
                        <span>Etiquetas</span>
                      </div>
                    ) : null}

                    {canManageStages ? (
                      <div onClick={handleOpenStages} className="option">
                        <i className="pi pi-clone" />
                        <span>Gerenciar etapas</span>
                      </div>
                    ) : null}

                    {canUpdatePipeline ? (
                      <div onClick={handleEditPipeline} className="option">
                        <i className="pi pi-sliders-h" />
                        <span>Editar pipeline</span>
                      </div>
                    ) : null}

                    {canAddPipeline ? (
                      <div onClick={handleAddPipeline} className="option">
                        <i className="pi pi-plus" />
                        <span>Adicionar pipeline</span>
                      </div>
                    ) : null}

                    {canUpdatePipeline ? (
                      <div onClick={handleOpenLossReason} className="option">
                        <i className="pi pi-exclamation-circle" />
                        <span>Motivos de não conversão</span>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </Adjustments>
            ) : null}
          </Title>

          {hasSelectedWorkspaces ? (
            <DragDropContext onDragEnd={handleDragEnd}>
              <StagesScroll>
                <StagesRow
                  style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "flex-start"
                  }}
                >
                  {stages.map((stage) => (
                    <StageColumn key={stage.id}>
                      <CardTitle style={{ cursor: "default" }}>
                        <div className="name">{stage.name}</div>
                      </CardTitle>

                      <hr
                        style={{
                          border: `1px solid ${stage.color}`,
                          width: "100%"
                        }}
                      />

                      <Droppable droppableId={String(stage.id)} type="CARD">
                        {(dropProvided, snapshot) => (
                          <StageCardsList
                            ref={dropProvided.innerRef}
                            {...dropProvided.droppableProps}
                            style={{
                              background: snapshot.isDraggingOver
                                ? "rgba(0, 0, 0, 0.03)"
                                : "transparent"
                            }}
                            onScroll={(e) => {
                              const target = e.currentTarget;

                              const reachedBottom =
                                Math.ceil(
                                  target.scrollTop + target.clientHeight
                                ) >= target.scrollHeight;

                              if (!reachedBottom) return;
                              if (loadingMoreStages[stage.id]) return;
                              if (finishedStages[stage.id]) return;

                              handleLoadMoreCards(stage.id);
                            }}
                          >
                            {stage.cards?.map((card, index) => (
                              <Draggable
                                key={card.id}
                                draggableId={String(card.id)}
                                index={index}
                              >
                                {(dragProvided, dragSnapshot) => (
                                  <div
                                    ref={dragProvided.innerRef}
                                    {...dragProvided.draggableProps}
                                    {...dragProvided.dragHandleProps}
                                    style={{
                                      ...dragProvided.draggableProps.style,
                                      opacity: dragSnapshot.isDragging
                                        ? 0.85
                                        : 1
                                    }}
                                  >
                                    {renderCard(card)}
                                  </div>
                                )}
                              </Draggable>
                            ))}

                            {dropProvided.placeholder}
                          </StageCardsList>
                        )}
                      </Droppable>

                      {user.admin ||
                      hasAnyPermission("contactPipeline_create") ? (
                        <CreateCard onClick={() => createCard(stage.id)}>
                          +
                        </CreateCard>
                      ) : null}
                    </StageColumn>
                  ))}
                </StagesRow>
              </StagesScroll>
            </DragDropContext>
          ) : null}
        </>
      )}

      <Sidebar
        visible={showEditStage}
        icons="Gerenciar etapas"
        position="right"
        onHide={() => {
          setShowEditStage(false);
        }}
        style={{ width: "410px" }}
        className="wsDialog"
      >
        <UpdateStage
          stages={stages}
          onClose={() => {
            setShowEditStage(false);
          }}
          onSuccess={() => {
            getAllStages();
            setShowEditStage(false);
          }}
          pipelineId={currentPipelineId ?? 0}
        />
      </Sidebar>

      <Sidebar
        visible={showCardSidebar}
        position="right"
        onHide={() => {
          setShowCardSidebar(false);
          setCardSelected(null);
          setCreateStageId(null);
          dispatch(setShowCreateContact(false));
        }}
        style={{ width: "410px" }}
        className="wsDialog noTitleSidebar"
      >
        <UpdateCard
          mode={cardMode}
          card={cardSelected}
          pipelineId={currentPipelineId ?? 0}
          workspaceId={
            selectedPipeline?.workspaceId ?? workspacesSelected?.[0] ?? null
          }
          visible={showCardSidebar}
          stages={stages}
          initialStageId={createStageId}
          cardLabels={labels.filter((label) =>
            cardSelected?.labelIds?.includes(label.id)
          )}
          allLabels={labels}
          allLossReasons={lossReasons}
          bots={bots}
          onClose={() => {
            setShowCardSidebar(false);
            setCardSelected(null);
            dispatch(setShowCreateContact(false));
          }}
          onSuccess={() => {
            setShowCardSidebar(false);
            setCardSelected(null);
            dispatch(setShowCreateContact(false));
            getAllStages();
          }}
        />
      </Sidebar>

      <Sidebar
        visible={showEditLabel}
        icons="Gerenciar etiquetas"
        position="right"
        onHide={() => {
          setShowEditLabel(false);
        }}
        style={{ width: "410px" }}
        className="wsDialog"
      >
        <UpdateLabel
          labels={labels}
          onClose={() => {
            setShowEditLabel(false);
          }}
          onSuccess={() => {
            getAllPipelineLabels();
          }}
        />
      </Sidebar>

      <Sidebar
        visible={showFiltersSidebar}
        icons="Filtros"
        position="right"
        onHide={() => {
          setShowFiltersSidebar(false);
        }}
        style={{ width: "410px" }}
        className="wsDialog"
      >
        <FilterSidebarContent>
          <FilterFields>
            <MultiSelect
              value={draftResponsibleUserIds}
              options={usersByWorkspace}
              optionLabel="name"
              optionValue="id"
              placeholder="Responsável"
              onChange={(e) => setDraftResponsibleUserIds(e.value ?? [])}
              className="filterField"
            />

            <LabelMultiSelectWrapper>
              <MultiSelect
                value={draftLabelIds}
                options={labelOptions}
                optionLabel="label"
                optionValue="value"
                showSelectAll={false}
                panelClassName="label-multiselect-panel"
                appendTo="self"
                placeholder="Etiquetas"
                display="chip"
                className="labelFilter"
                onChange={(e) => setDraftLabelIds(e.value ?? [])}
                itemTemplate={(option) => (
                  <LabelChip
                    bgColor={option.color}
                    isLight={isLightColor(option.color)}
                  >
                    {option.label}
                  </LabelChip>
                )}
                selectedItemTemplate={(value: number) => {
                  const option = labelOptions.find(
                    (opt) => opt.value === value
                  );
                  if (!option) return null;

                  return (
                    <LabelChip
                      bgColor={option.color}
                      isLight={isLightColor(option.color)}
                    >
                      {option.label}
                    </LabelChip>
                  );
                }}
                panelHeaderTemplate={() => null}
              />
            </LabelMultiSelectWrapper>

            <Dropdown
              value={draftSource}
              options={sourceOptions}
              placeholder="Origem"
              appendTo="self"
              onChange={(e) => setDraftSource(e.value ?? "")}
              className="sourceFilter"
            />
          </FilterFields>

          <FilterButtons>
            <Button
              className="clearFiltersButton"
              label="LIMPAR FILTROS"
              onClick={handleClearFilters}
            />
            <Button
              onClick={handleApplyFilters}
              style={{
                background: user.plataform.mainColor
                  ? user.plataform.mainColor
                  : "#2624CD",
                borderColor: user.plataform.mainColor
                  ? user.plataform.mainColor
                  : "#2624CD",
                color: "#fff"
              }}
            >
              APLICAR FILTROS
            </Button>
          </FilterButtons>
        </FilterSidebarContent>
      </Sidebar>

      <Sidebar
        visible={showPipelineSidebar}
        icons={pipelineSelected?.id ? "Editar pipeline" : "Adicionar pipeline"}
        position="right"
        onHide={() => {
          setShowPipelineSidebar(false);
          setPipelineSelected(null);
        }}
        style={{ width: "410px" }}
        className="wsDialog"
      >
        <UpdatePipeline
          pipeline={pipelineSelected}
          onClose={() => {
            setShowPipelineSidebar(false);
            setPipelineSelected(null);
          }}
          onSuccess={() => {
            setShowPipelineSidebar(false);
            setPipelineSelected(null);
            getPipelines();
            getMainPipeline();
          }}
        />
      </Sidebar>

      <Sidebar
        visible={showLossReasonSidebar}
        icons="Motivos de não conversão"
        position="right"
        onHide={() => {
          setShowLossReasonSidebar(false);
        }}
        style={{ width: "410px" }}
        className="wsDialog"
      >
        <LossReason
          lossReasons={lossReasons}
          onClose={() => {
            setShowLossReasonSidebar(false);
          }}
          onSuccess={() => {
            handleGetCardLossReasons();
          }}
        />
      </Sidebar>
    </Container>
  );
};

export default Pipeline;
